import { App as AntApp, Button, Card, Popconfirm, Space, Table, Tabs, Tag, Typography } from 'antd';
import { DeleteOutlined, FileSearchOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDateTime } from '@/lib/format';
import { AdsModerationTab } from './AdsModerationTab';
import {
  useModerationReportsQuery,
  useRemoveListingMutation,
  useResolveReportMutation,
  useRestoreListingMutation,
  type AdminListingReport,
  type ListingReportResolution,
  type ModerationReportsState,
  type VisionListingStatus,
} from './hooks';

const { Title, Text } = Typography;

// Статус объявления в очереди. exhaustive-switch по VD8: `removed` в очереди
// встречается редко (remove авторезолвит жалобы), но форма обязана его знать.
function ListingStatusTag({ status }: { status: VisionListingStatus }): JSX.Element {
  const { t } = useTranslation();
  switch (status) {
    case 'active':
      return <Tag color="green">{t('platform.moderation.listing_status.active')}</Tag>;
    case 'archived':
      return <Tag>{t('platform.moderation.listing_status.archived')}</Tag>;
    case 'removed':
      return <Tag color="red">{t('platform.moderation.listing_status.removed')}</Tag>;
  }
}

// Исход разбора жалобы (журнал, №18): снято / не подтвердилась.
function ResolutionTag({ resolution }: { resolution: ListingReportResolution }): JSX.Element {
  const { t } = useTranslation();
  switch (resolution) {
    case 'removed':
      return <Tag color="red">{t('platform.moderation.resolution.removed')}</Tag>;
    case 'dismissed':
      return <Tag>{t('platform.moderation.resolution.dismissed')}</Tag>;
  }
}

/** Вкладки страницы: две по жалобам + «Реклама» (V1.4, свой компонент и свои `?state=`/`?page=`). */
type ModerationTab = ModerationReportsState | 'ads';
const TAB_KEYS: readonly ModerationTab[] = ['open', 'resolved', 'ads'];

function parseTab(raw: string | null): ModerationTab {
  return (TAB_KEYS as readonly string[]).includes(raw ?? '') ? (raw as ModerationTab) : 'open';
}

// Раздел «Модерация» (V1.3-d) — жалобы на объявления Starling Vision.
// Две вкладки (`?tab=open|resolved`, доводка приёмки №18):
//   * «Очередь» — неразобранные, FIFO (старые сверху, ничто не «тонет»);
//     действия: снять объявление (Popconfirm; бэкенд авторезолвит все его
//     открытые жалобы), закрыть жалобу без снятия;
//   * «Журнал» — разобранные, свежие решения сверху; колонка «Решение»
//     (исход + когда + кто); «Восстановить» для снятых модерацией — раньше
//     после авторезолва жалоб при remove кнопки было не найти (снятие было
//     необратимо из UI). archived (снял автор) бэкенд отбивает 409.
// Третья вкладка «Реклама» (V1.4) — заявки на баннеры Vision, см. AdsModerationTab.
// Страничная пагинация как в журнале SMS. Данные — /api/v1/vision/admin/* (VD10).
export function ModerationPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = parseTab(searchParams.get('tab'));
  const page = Number(searchParams.get('page') ?? '1') || 1;
  const pageSize = Number(searchParams.get('page_size') ?? '20') || 20;

  const reportsState: ModerationReportsState = tab === 'ads' ? 'open' : tab;
  const { data, isLoading, error } = useModerationReportsQuery(
    { state: reportsState, page, pageSize },
    { enabled: tab !== 'ads' },
  );
  const removeMutation = useRemoveListingMutation();
  const restoreMutation = useRestoreListingMutation();
  const resolveMutation = useResolveReportMutation();

  const handleRemove = (r: AdminListingReport): void => {
    removeMutation.mutate(
      { listingId: r.listing_id },
      {
        onSuccess: () => void message.success(t('platform.moderation.remove_ok')),
        onError: (err) =>
          void message.error(err instanceof Error ? err.message : t('errors.generic')),
      },
    );
  };

  const handleRestore = (r: AdminListingReport): void => {
    restoreMutation.mutate(
      { listingId: r.listing_id },
      {
        onSuccess: () => void message.success(t('platform.moderation.restore_ok')),
        onError: (err) => {
          // 409 listing_not_removed: объявление снято АВТОРОМ (archived) —
          // админ решение автора не перебивает (VD10).
          if (err instanceof ApiError && err.code === 'listing_not_removed') {
            void message.warning(t('platform.moderation.restore_not_removed'));
          } else {
            void message.error(err instanceof Error ? err.message : t('errors.generic'));
          }
        },
      },
    );
  };

  const handleResolve = (r: AdminListingReport): void => {
    resolveMutation.mutate(
      { reportId: r.id },
      {
        onSuccess: () => void message.success(t('platform.moderation.resolve_ok')),
        onError: (err) =>
          void message.error(err instanceof Error ? err.message : t('errors.generic')),
      },
    );
  };

  const baseColumns: ColumnsType<AdminListingReport> = [
    {
      title: t('platform.moderation.table.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
    {
      title: t('platform.moderation.table.reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: t('platform.moderation.table.listing'),
      key: 'listing',
      render: (_v, r) => (
        <Space direction="vertical" size={4}>
          <Text strong style={{ fontSize: 13 }}>
            {r.listing_title}
          </Text>
          <ListingStatusTag status={r.listing_status} />
        </Space>
      ),
    },
    {
      title: t('platform.moderation.table.organization'),
      dataIndex: 'organization_name',
      key: 'organization',
      width: 180,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('platform.moderation.table.reporter'),
      dataIndex: 'reporter_account_id',
      key: 'reporter',
      width: 130,
      render: (v: string | null) =>
        v === null ? (
          <Text type="secondary" italic>
            {t('platform.moderation.anonymous')}
          </Text>
        ) : (
          <Text className="sl-mono" title={v} style={{ fontSize: 12 }}>
            {v.slice(0, 8)}…
          </Text>
        ),
    },
  ];

  const restoreButton = (r: AdminListingReport): JSX.Element => (
    <Button
      size="small"
      icon={<UndoOutlined />}
      loading={restoreMutation.isPending && restoreMutation.variables?.listingId === r.listing_id}
      onClick={() => handleRestore(r)}
    >
      {t('platform.moderation.restore')}
    </Button>
  );

  const queueColumns: ColumnsType<AdminListingReport> = [
    ...baseColumns,
    {
      title: t('platform.moderation.table.action'),
      key: 'action',
      width: 220,
      align: 'right',
      render: (_v, r) => (
        <Space direction="vertical" size={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
          {r.listing_status === 'removed' ? (
            restoreButton(r)
          ) : (
            <Popconfirm
              title={t('platform.moderation.remove_confirm')}
              okText={t('platform.moderation.remove')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleRemove(r)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={
                  removeMutation.isPending && removeMutation.variables?.listingId === r.listing_id
                }
              >
                {t('platform.moderation.remove')}
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<FileSearchOutlined />}
            loading={resolveMutation.isPending && resolveMutation.variables?.reportId === r.id}
            onClick={() => handleResolve(r)}
          >
            {t('platform.moderation.resolve')}
          </Button>
        </Space>
      ),
    },
  ];

  const journalColumns: ColumnsType<AdminListingReport> = [
    ...baseColumns,
    {
      title: t('platform.moderation.table.resolution'),
      key: 'resolution',
      width: 220,
      render: (_v, r) => (
        <Space direction="vertical" size={4}>
          {r.resolution !== null && <ResolutionTag resolution={r.resolution} />}
          <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
            {r.resolved_at !== null ? formatDateTime(r.resolved_at) : ''}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.resolved_by_account_id !== null
              ? t('platform.moderation.resolved_by', {
                  id: `${r.resolved_by_account_id.slice(0, 8)}…`,
                })
              : t('platform.moderation.resolved_by_unknown')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('platform.moderation.table.action'),
      key: 'action',
      width: 160,
      align: 'right',
      // Восстановить можно только снятое модерацией; активное/архивное — пусто.
      render: (_v, r) => (r.listing_status === 'removed' ? restoreButton(r) : null),
    },
  ];

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  const onTabChange = (key: string): void => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', parseTab(key));
    sp.delete('page');
    sp.delete('state');
    setSearchParams(sp);
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('platform.moderation.title')}
      </Title>
      <Text type="secondary">{t('platform.moderation.subtitle')}</Text>

      {tab !== 'ads' && error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <Card style={{ marginTop: 16 }} variant="outlined">
        <Tabs
          activeKey={tab}
          onChange={onTabChange}
          items={[
            { key: 'open', label: t('platform.moderation.tabs.queue') },
            { key: 'resolved', label: t('platform.moderation.tabs.journal') },
            { key: 'ads', label: t('platform.moderation.tabs.ads') },
          ]}
        />
      </Card>

      {tab === 'ads' && <AdsModerationTab />}

      {tab !== 'ads' && (
        <Card style={{ marginTop: 16 }} variant="outlined" styles={{ body: { padding: 0 } }}>
          <Table<AdminListingReport>
            rowKey="id"
            loading={isLoading}
            dataSource={items}
            columns={tab === 'open' ? queueColumns : journalColumns}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              onChange: (p, ps) => {
                const sp = new URLSearchParams(searchParams);
                sp.set('page', String(p));
                sp.set('page_size', String(ps));
                setSearchParams(sp);
              },
            }}
            locale={{
              emptyText:
                tab === 'open'
                  ? t('platform.moderation.empty')
                  : t('platform.moderation.journal_empty'),
            }}
          />
        </Card>
      )}
    </div>
  );
}
